/**
 * Goal-settings AI helpers — validate GOAL_PROPOSAL vs 実績 and build auto-propose prompts.
 * Card numbers must come from AI, never from computeSuggestedTarget (40%-pull).
 * 注力 has up to three slots (①②③, FOCUS_SLOTS); the 追加KGI is entered by hand, never proposed.
 */

import { FOCUS_SLOTS, getFocusDef } from './kpiFocusCatalog';

/** Short identity/driver sheet for【目標相談】kpi_summary. */
export const KPI_IDENTITY_CHEATSHEET = [
  '【KPIの関係】',
  '売上 ≒ 客数 × 客単価。技術売上 + 店販 が売上の主な内訳。',
  '目標差は全レバーに重み付きで配分して逆算する。設定する注力（1〜3つ）で合わせて約6割（①>②>③。注力1つなら約4割まで）、残りは他レバー。注力には配分した担当分だけを載せる（1指標で全額を埋めない・配分以上に上げない）。',
  '指名率 = 指名客 ÷ 総客数。指名客数 = 客数 × 指名率（客数が動かなければ指名率だけで決まる）。',
  '新規再来率 = 3ヶ月前に新規来店した客のうち90日以内に再来した割合（当月新規とは別の集団）。',
  '来店周期(日)は小さいほど良い（down）。他の注力レバーは大きいほど良い（up）。',
  '下限は当月現状。過去最高より小さい値を却下するな。ユーザーがカードで直した数字は制限しない。',
  '【当月は未完了】があるとき、当月は暦としてまだ終わっていない途中集計。逆算・実力の基準は直近完了月（次の月に入った＝最後の日が過ぎた月）。当月の率・人数を月次水準として語るな。',
  '【注力の選び方・固定ルール】',
  '① KGIの指標（売上、追加KGIの指名率・指名数）は注力に選ばない。①②③はすべて異なる指標。',
  '② 指名率が80%未満なら、注力①は必ず指名率(nomination)。他にGAPが大きい指標があっても指名率が先。'
    + 'ただし指名率が追加KGIのときはこのルールは使わない（指名率は注力に選べない）。',
  '③ 新規再来率(newReturn)は提案しない。実績が0%でも選ばない（集計が3ヶ月前のコホートで、'
    + 'スタイリスト単位では1〜3人が母数のため0%が実態を表さない）。ユーザーが自分で選んだ時だけ扱う。',
].join('\n');

/**
 * True when target is a real improvement vs 実績 for this KPI direction.
 * up → target > actual; down → target < actual.
 * Missing numbers → false (do not accept).
 */
export function isImprovementTarget(better, target, actual) {
  if (target == null || actual == null) return false;
  const t = Number(target);
  const a = Number(actual);
  if (!Number.isFinite(t) || !Number.isFinite(a)) return false;
  if (better === 'down') return t < a;
  return t > a;
}

/** Actual-value field in `actuals` for each slot: focusActual / subFocusActual / thirdFocusActual. */
const ACTUAL_FIELD = { 1: 'focusActual', 2: 'subFocusActual', 3: 'thirdFocusActual' };

/**
 * Validate pending GOAL_PROPOSAL focus targets against current 実績.
 * `kgiKeys` (売上 + 追加KGI) are never valid 注力 keys — the save would be refused.
 * @returns {{ ok: boolean, reason?: string }}
 */
export function validateGoalProposalTargets(proposal, actuals = {}, { kgiKeys = [] } = {}) {
  if (!proposal || proposal.kind !== 'goals') {
    return { ok: false, reason: 'GOAL_PROPOSAL がありません' };
  }
  const checks = FOCUS_SLOTS.filter(
    (s) => proposal[s.keyField] != null || proposal[s.targetField] != null
  ).map((s) => ({
    key: proposal[s.keyField],
    target: proposal[s.targetField],
    actual: actuals[ACTUAL_FIELD[s.slot]],
    label: `注力${s.label}`,
  }));
  if (proposal.finalSales != null && actuals.salesActual != null) {
    if (!isImprovementTarget('up', proposal.finalSales, actuals.salesActual)) {
      return {
        ok: false,
        reason: `売上ゴールは現状実績より大きくしてください（現状 ${actuals.salesActual}）`,
      };
    }
  }
  for (const c of checks) {
    if (c.key && kgiKeys.includes(c.key)) {
      return {
        ok: false,
        reason: `${c.label}に ${getFocusDef(c.key)?.label || c.key} は選べません（KGIの指標）。別のKPIに変えてください`,
      };
    }
    if (c.target == null) {
      return { ok: false, reason: `${c.label} の数値がありません` };
    }
    if (c.actual == null) {
      return { ok: false, reason: `${c.label} の現状実績が無いため提案を検証できません` };
    }
    const def = getFocusDef(c.key);
    const better = def?.better || 'up';
    // Floor is **this month's 実績 only**. Historical 最高 is reference, never a reject.
    if (!isImprovementTarget(better, c.target, c.actual)) {
      const dir = better === 'down' ? '小さく' : '大きく';
      return {
        ok: false,
        reason: `${c.label}（${def?.label || c.key || 'KPI'}）は現状 ${c.actual}より${dir}してください（提案 ${c.target}）`,
      };
    }
  }
  return { ok: true };
}

const STANCE_HINT = {
  standard: '標準の伸びしろで',
  aggressive: 'やや強めの伸びしろで',
  conservative: '慎重めの伸びしろで',
};

const SCOPE_SLOT = { kpi1: 1, kpi2: 2, kpi3: 3 };

/**
 * Auto prompt for GoalEdit card propose (not free-form user text).
 *
 * @param scope 'kpi1' | 'kpi2' | 'kpi3' — one slot; 'kpi' / null — several slots at once
 * @param metrics [card①, card②, card③] from buildFocusMetricCard (unselected cards have isSelected false)
 * @param targets [t①, t②, t③] — formatted decided targets, or null
 * @param slots   for scope 'kpi': the slot numbers to propose (default [1, 2])
 * @param subKgi  { key, label } of the 追加KGI, or null — never a 注力 key
 * @param swapKey 「別の案で再計算」— the lever itself must change, not just the number.
 *   Without it the model is told 「focusTarget のみ」and keeps re-pricing the same KPI.
 */
export function buildAutoProposePrompt({
  scope,
  metrics = [],
  targets = [],
  slots = null,
  subKgi = null,
  stance = 'standard',
  swapKey = false,
} = {}) {
  const stanceJa = STANCE_HINT[stance] || STANCE_HINT.standard;
  const lines = [
    '改善目標の提案をお願いします。',
    `${stanceJa}、スタイリスト本人の実績・GAP・予測だけを使ってください。`,
    '本文で注力指標名を明示し、「あなたはこのままだとこう」（予測に基づく一文）を必ず書いてから改善目標を示してください。',
    'GOAL_PROPOSAL を必ず付けてください。',
  ];
  if (subKgi?.key) {
    lines.push(
      `追加KGIは「${subKgi.label}」(key=${subKgi.key})。KGIなので注力には選ばない（売上 totalSales も同じ）。`
    );
  }
  const slotOf = (n) => FOCUS_SLOTS[n - 1];
  const metricOf = (n) => metrics[n - 1] || null;
  const takenBy = (n) =>
    FOCUS_SLOTS.filter((s) => s.slot !== n && metricOf(s.slot)?.isSelected).map(
      (s) => `注力${s.label}の ${metricOf(s.slot).key}`
    );

  const one = SCOPE_SLOT[scope];
  if (one) {
    const s = slotOf(one);
    const m = metricOf(one);
    // Decided numbers on the other slots are constraints, not context: this slot must be
    // derivable from them through 【KPIの関係】, or the proposal is rejected as inconsistent.
    const decided = FOCUS_SLOTS.filter(
      (o) => o.slot !== one && metricOf(o.slot)?.isSelected && targets[o.slot - 1] != null
    );
    decided.forEach((o) => {
      const om = metricOf(o.slot);
      lines.push(
        `注力${o.label}は「${om.label}」(key=${om.key}) 現状 ${om.value} → 改善目標 ${targets[o.slot - 1]} で確定済み。`
      );
    });
    if (decided.length) {
      lines.push(
        `注力${s.label}はこれらと【KPIの関係】で整合させる: 確定済みの改善目標と基準月の他レバーから値を計算し、その計算値から大きく離れた数字は出さない。` +
          '基準月は【当月は未完了】があれば直近完了月、なければ当月。' +
          '例: 指名率 44%→48%・客数25なら 指名客数 ≈ 25×0.48 = 12。24 を出すなら客数50が前提で矛盾する。' +
          '客数を増やす必要があるなら 指名客数 ではなく 客数(customerCount) にする。' +
          '本文に計算式を1つ書く。'
      );
    }
    const others = takenBy(one);
    const notSame = others.length ? `${others.join(' / ')} と同じkeyは不可。` : '';
    if (swapKey && m?.isSelected) {
      lines.push(
        `注力${s.label}は「${m.label}」(key=${m.key}) **以外**の指標に変えてください。` +
          `GAP候補から別のkeyを1つ選び、GOAL_PROPOSAL に ${s.keyField}+${s.targetField} を必ず入れる。` +
          notSame +
          '提案値は新しい指標の現状実績より改善方向へ。'
      );
    } else if (m?.isSelected) {
      lines.push(
        `注力${s.label}「${m.label}」(key=${m.key}) の ${s.targetField} のみ。` +
          `現状実績 ${m.value}。提案値は実績より改善方向へ（upなら大きく / downなら小さく）。`
      );
    } else {
      lines.push(
        `注力${s.label}は未選択。GAP候補から1つ選び、GOAL_PROPOSAL に ${s.keyField}+${s.targetField} を必ず入れる。` +
          notSame +
          '提案値は現状実績より改善方向へ。'
      );
    }
    return lines.join('\n');
  }

  // Several slots at once (「注力指標」 menu / first proposal)
  const want = (slots && slots.length ? slots : [1, 2]).filter((n) => slotOf(n));
  want.forEach((n) => {
    const s = slotOf(n);
    const m = metricOf(n);
    if (m?.isSelected) {
      lines.push(`注力${s.label}「${m.label}」(key=${m.key}) 現状 ${m.value}`);
    } else {
      lines.push(`注力${s.label}は未選択 → GAP候補から ${s.keyField}+${s.targetField} を選ぶ（他の注力と異なるkey）。`);
    }
  });
  const swapAway = swapKey
    ? want.filter((n) => metricOf(n)?.isSelected).map((n) => `注力${slotOf(n).label}の ${metricOf(n).key}`)
    : [];
  if (swapAway.length) {
    lines.push(
      `今回は指標そのものを変えてください。${swapAway.join(' / ')} は使わず、` +
        'GAP候補から別のkeyを選び、各枠の key を GOAL_PROPOSAL に必ず入れてください（注力どうしで同じkeyは不可）。'
    );
  }
  lines.push(`${want.map((n) => `注力${slotOf(n).label}`).join('・')}それぞれの改善目標を GOAL_PROPOSAL に入れてください。`);
  return lines.join('\n');
}

/** Map pending goals proposal → card slot fields (①②③ + KGI/period). */
export function mapProposalToCardFields(proposal) {
  if (!proposal || proposal.kind !== 'goals') return null;
  const num = (v) => (v != null ? Number(v) : null);
  return {
    focusKey: proposal.focusKey ?? null,
    focusTarget: num(proposal.focusTarget),
    subFocusKey: proposal.subFocusKey ?? null,
    subFocusTarget: num(proposal.subFocusTarget),
    thirdFocusKey: proposal.thirdFocusKey ?? null,
    thirdFocusTarget: num(proposal.thirdFocusTarget),
    finalSales: num(proposal.finalSales),
    planLength: num(proposal.planLength),
    why: (proposal.why || '').trim() || null,
  };
}
